import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { DropdownItem } from "models/order-management/dropdownItem";
import { Order } from "models/order-management/models";
import { OrderComponent } from "models/order-management/orderComponent";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

const generalInPatient = "generalInPatient";

@Component({
  selector: "reasons-for-gip",
  templateUrl: "reasons-for-gip.html",
})
export class ReasonsForGIPComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState() {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  private _orderFormState: OrderFormState;
  reasonsForGIPOther: string = "";
  isGeneralInPatient = false;
  reasonsForGIPSelected: string[] = [];
  reasonsForGIP: DropdownItem[] = [];

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.setReasonsForGIP(this.orderComponent);
    this._orderFormState.formType === "amendment"
      ? this.setAmendedValue()
      : null;
    this.checkIsValid();
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get isOtherSelected() {
    return this.reasonsForGIPSelected.includes("other");
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;
    this.isGeneralInPatient = orderFormState.levelOfCare === generalInPatient;
    if (!this.isGeneralInPatient) {
      this.reasonsForGIPSelected = [];
      this.reasonsForGIPOther = "";
      this.orderCreateRequestEvent.emit({
        deletes: ["reasonsForGIP", "reasonsForGIPOther"],
      });
    }
    this.checkIsValid();
  }

  setReasonsForGIP(orderComponent: OrderComponent) {
    if (orderComponent.properties[0]) {
      this.reasonsForGIP = orderComponent.properties[0].dropdownItems;
    }
  }

  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.reasonsForGIPSelected = [];
    let temp = this.orderCreateRequest[componentField]
      ? this.orderCreateRequest[componentField]
      : [];
    var matchedReasons = this.orderComponent.properties[0].dropdownItems.filter(
      (reason) => {
        return temp.includes(reason.value);
      }
    );
    matchedReasons.map((matchedReason) => {
      this.reasonsForGIPSelected.push(matchedReason.value);
    });
    this.reasonsForGIPOther = this.orderCreateRequest["reasonsForGIPOther"]
      ? this.orderCreateRequest["reasonsForGIPOther"]
      : "";
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  checkIsValid() {
    let reasonsForGIPOtherSelected =
      this.reasonsForGIPSelected.includes("other");
    let reasonsForGIPOtherValid = true;
    if (reasonsForGIPOtherSelected) {
      reasonsForGIPOtherValid = this.reasonsForGIPOther.length > 0;
    }
    if (
      this.isGeneralInPatient &&
      (this.reasonsForGIPSelected.length == 0 || !reasonsForGIPOtherValid)
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isRequired() {
    return this._orderFormState.levelOfCare === generalInPatient;
  }

  onChangeCheckbox(event: any, option: DropdownItem) {
    if (event) {
      if (event.target.checked) {
        this.reasonsForGIPSelected.push(option.value);
      } else {
        this.reasonsForGIPSelected = this.reasonsForGIPSelected.filter(
          (value) => value !== option.value
        );
      }

      if (!this.isOtherSelected) {
        this.reasonsForGIPOther = "";
      }

      if (
        this.orderComponent.properties[0].amendable &&
        this._orderFormState.formType === "amendment"
      ) {
        const componentField = this.orderComponent.properties[0].name;
        const prevValue = this.orderCreateRequest[componentField];
        if (prevValue !== this.reasonsForGIPSelected) {
          const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
          let temp = this.orderCreateRequest[componentField];
          var matchedReasons = this.reasonsForGIP.filter((reason) => {
            return temp.includes(reason.value);
          });
          amendedObject.updates = {
            propertyName: componentField,
            previousValueDisplay: matchedReasons
              .map((item) => `${item.label} ${item.labelDescription}`)
              .join(", "),
            previousValue: this.orderCreateRequest[componentField].join(", "),
            newValue: this.reasonsForGIPSelected,
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        } else {
          const amendedObject: OrderComponentPropertyUpdate = {
            updates: {},
            deletes: [componentField as keyof Order],
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        }
      } else {
        this.orderCreateRequestEvent.emit({
          updates: {
            reasonsForGIP: this.reasonsForGIPSelected,
            reasonsForGIPOther: this.reasonsForGIPOther,
          },
        });
      }
      this.checkIsValid();
    }
  }

  onReasonsForGIPOtherChange(reason: string) {
    if (this.isOtherSelected) {
      this.checkIsValid();
    }
    if (
      this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    ) {
      const componentField = this.orderComponent.properties[0].name;
      const prevValue = this.orderCreateRequest[componentField].toString();
      if (prevValue !== reason) {
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        amendedObject.updates = {
          propertyName: "reasonsForGIPOther",
          previousValueDisplay: this.orderCreateRequest["reasonsForGIPOther"],
          previousValue: this.orderCreateRequest["reasonsForGIPOther"],
          newValue: reason,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: ["reasonsForGIPOther"],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else {
      this.orderCreateRequestEvent.emit({
        updates: {
          reasonsForGIP: this.reasonsForGIPSelected,
          reasonsForGIPOther: reason,
        },
      });
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/reasons-for-gip/reasons-for-gip.ts